import React, { memo } from "react";
import { Button, Input, InputNumber, Typography } from "antd";
import {
  CalculatorOutlined,
  CloseOutlined,
  EditOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { SCENARIO_FIELDS, ModifiedTag } from "../PlaygroundUtils";

const { Text } = Typography;

const ScenarioSettingsCard = memo(function ScenarioSettingsCard({
  liveValues,
  whatIfValues,
  isEditing,
  errorFields,
  onToggleEdit,
  onValueChange,
  onReset,
  onRunSimulation,
  isModified,
}) {
  return (
    <div className="box_wraper_div playground_scenario_card">
      <div className="playground_card_header">
        <div className="playground_card_header_left">
          <Text className="playground_card_title">Scenario Settings</Text>
          <Text className="playground_card_subtitle">
            {isEditing
              ? 'Modify Proposed values and click "Run Simulation" to simulate ranking changes.'
              : 'View current/live values. Click "Edit Proposed Values" to modify and run a simulation.'}
          </Text>
        </div>
        <Button
          className={`playground_edit_toggle_btn ${
            isEditing ? "playground_edit_toggle_btn--cancel" : ""
          }`}
          icon={isEditing ? <CloseOutlined /> : <EditOutlined />}
          onClick={onToggleEdit}
        >
          {isEditing ? "Cancel Edit" : "Edit"}
        </Button>
      </div>

      <div className="playground_scenario_table">
        <div
          className={`playground_scenario_row playground_scenario_head ${
            !isEditing ? "playground_scenario_view_mode" : ""
          }`}
        >
          <Text className="playground_col_head">Variable</Text>
          <Text className="playground_col_head">Current (Live)</Text>
          {isEditing && (
            <Text className="playground_col_head">Proposed (Editable)</Text>
          )}
        </div>

        {SCENARIO_FIELDS.map(({ key, label }) => (
          <div
            className={`playground_scenario_row ${
              !isEditing ? "playground_scenario_view_mode" : ""
            }`}
            key={key}
          >
            <Text className="playground_field_label">{label}</Text>
            <div className="playground_live_cell">
              <Input
                value={liveValues[key]}
                disabled
                className="playground_live_input"
              />
            </div>
            {isEditing && (
              <div className="playground_whatif_cell">
                <InputNumber
                  value={whatIfValues[key]}
                  min={0}
                  controls={false}
                  status={errorFields[key] ? "error" : ""}
                  onChange={(val) => {
                    if (typeof val === "string") {
                      const sanitized = val.replace(/[^\d.]/g, "");
                      onValueChange(key, sanitized === "" ? null : sanitized);
                    } else {
                      onValueChange(key, val);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onRunSimulation();
                      return;
                    }
                    if (
                      e.ctrlKey ||
                      e.metaKey ||
                      [
                        "Backspace",
                        "Delete",
                        "ArrowLeft",
                        "ArrowRight",
                        "ArrowUp",
                        "ArrowDown",
                        "Tab",
                        "Escape",
                        "Home",
                        "End",
                      ].includes(e.key)
                    ) {
                      return;
                    }
                    if (/[a-zA-Z]/.test(e.key)) {
                      e.preventDefault();
                      return;
                    }
                    if (!/[\d.]/.test(e.key)) {
                      e.preventDefault();
                      return;
                    }
                    if (
                      e.key === "." &&
                      String(whatIfValues[key] ?? "").includes(".")
                    ) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const paste = e.clipboardData?.getData("text") || "";
                    if (
                      /[a-zA-Z]/.test(paste) ||
                      !/^\d*(\.\d*)?$/.test(paste.trim())
                    ) {
                      e.preventDefault();
                    }
                  }}
                  parser={(value) => (value || "").replace(/[^\d.]/g, "")}
                  style={{ width: "100%" }}
                />
                {isModified(key) && <ModifiedTag />}
              </div>
            )}
            {isEditing && errorFields[key] && (
              <Text type="danger" italic className="playground_field_error">
                {errorFields[key]}
              </Text>
            )}
          </div>
        ))}
      </div>

      {isEditing && (
        <div className="playground_scenario_actions">
          <Button icon={<ReloadOutlined />} onClick={onReset}>
            Reset to Current
          </Button>
          <Button
            type="primary"
            icon={<CalculatorOutlined />}
            onClick={onRunSimulation}
          >
            Run Simulation
          </Button>
        </div>
      )}
    </div>
  );
});

export default ScenarioSettingsCard;
